import { Link } from "react-router-dom";
import {
  CheckCircle2, ShieldCheck, Sparkles, Building2, Users, Gauge, Zap, ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";

const PLAN_TIERS = [
  {
    key: "free",
    name: "Free",
    icon: Gauge,
    summary: "Try ConfirmPH with one car or up to three motorcycles.",
    bullets: [
      "Manual booking + confirmation links",
      "Owner + 1 employee account",
      "Basic public business profile",
      "Reminder + incident basics",
    ],
  },
  {
    key: "basic",
    name: "Basic",
    icon: Building2,
    summary: "For small rental shops that want confirmation links, reminders, and booking protection.",
    bullets: [
      "Everything in Free",
      "Larger fleet capacity",
      "Up to 3 staff accounts",
      "Reminder detection and staff-reviewed sending",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    icon: Sparkles,
    summary: "For growing rentals that want public visibility, more staff, and in-app bookings.",
    badge: "Trial default",
    bullets: [
      "Everything in Basic",
      "Administration workspace and Finance & Books (internal, accountant-ready records, not BIR-registered books)",
      "In-app booking requests",
      "Website Integration: hosted booking page + embeddable widget",
      "Public Discover Rentals listing eligibility",
      "Up to 10 staff accounts",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    icon: ShieldCheck,
    summary: "For larger fleets and operators needing advanced controls.",
    bullets: [
      "Everything in Pro",
      "Unlimited fleet & staff",
      "Planned: multi-branch support",
      "Planned: public API",
      "Planned: priority support",
    ],
  },
];


export default function BusinessPricing() {
  return (
    <div className="bg-white" data-testid="business-pricing-page">
      <SEO
        title="Business Plans & Pricing: ConfirmPH"
        description="Plans for Philippine rental businesses: confirmation links, reminders, in-app bookings, multi-staff workspaces, and an Enterprise mini-site."
        canonical="/businesses/pricing"
      />
      {/* HERO */}
      <section className="px-4 sm:px-6 lg:px-8 pt-14 pb-10 max-w-5xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
          <ShieldCheck className="w-3.5 h-3.5" /> Plans for rental businesses
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold leading-tight">
          Built for Philippine rental businesses.
        </h1>
        <p className="mt-3 text-base text-slate-700 leading-relaxed max-w-2xl">
          ConfirmPH gives small businesses a simple way to truly confirm bookings, reduce no-shows,
          and look more trustworthy to customers. Plans scale with your fleet and team, and you
          can keep using ConfirmPH free forever for a single car or up to three motorcycles.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link to="/register?as=business" data-testid="bp-cta-trial"
                 className="inline-flex items-center px-5 py-2.5 rounded-lg btn-brand font-medium text-sm">
            Start 30-day free trial <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
          <Link to="/login?as=business" data-testid="bp-cta-login"
                 className="inline-flex items-center px-5 py-2.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium">
            Business login
          </Link>
          <Link to="/businesses" data-testid="bp-cta-learn-more"
                 className="text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4">
            Learn more about ConfirmPH for business
          </Link>
        </div>
      </section>

      {/* WHY-NO-PRICE EXPLAINER */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-testid="bp-private-pricing">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-display text-lg sm:text-xl font-semibold text-amber-900">
                Business plan pricing is shown inside your dashboard
              </h2>
              <p className="text-sm text-amber-900/90 mt-1 leading-relaxed">
                We deliberately do not display exact business plan prices on a public page.
                Once you sign up, your owner dashboard shows the current Basic, Pro, and Enterprise
                pricing with full feature comparisons, fleet-point capacity, and an instant trial
                countdown, so you only see the details that matter to your business.
              </p>
              <ul className="mt-3 text-sm text-amber-900/90 space-y-1">
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
                  Free plan stays available, even after your 30-day Pro trial ends.
                </li>
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
                  Full Basic / Pro / Enterprise pricing visible once you log in.
                </li>
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
                  No surprise charges: nothing is charged automatically. Each paid period is a
                  payment you start yourself.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TIER OVERVIEW */}
      <section className="px-4 sm:px-6 lg:px-8 mt-12 max-w-5xl mx-auto" data-testid="bp-tier-overview">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">Four tiers, same product</h2>
        <p className="text-sm text-slate-600 mt-1">
          Every plan uses the same ConfirmPH workflow. You grow into higher tiers as your fleet,
          team, and visibility needs grow.
        </p>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {PLAN_TIERS.map(({ key, name, icon: Icon, summary, bullets, badge }) => (
            <div key={key} data-testid={`bp-tier-${key}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#1B4332]/30 transition">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#1B4332]/10 text-[#1B4332] flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{name}</h3>
                </div>
                {badge && (
                  <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                    {badge}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-700 mt-2 leading-relaxed">{summary}</p>
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-1.5 text-xs text-slate-600">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-500 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-500 mt-3 italic">
                Exact pricing available in your dashboard after sign-up.
              </p>
            </div>
          ))}
        </div>
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-2"
             data-testid="bp-every-plan">
          <p>
            <span className="font-semibold text-slate-900">On every plan: </span>
            Business and Fleet Legal Readiness, company drivers and dispatch, authorized self-drive
            drivers, staff permissions, and the full rental workflow from agreement and handover to
            return, damage, settlement and closure. Required readiness and safety checks are never
            reserved for a higher plan, and no plan changes a verification, legal-readiness or
            safety status.
          </p>
          <p>
            <span className="font-semibold text-slate-900">Add-ons: </span>
            monthly fleet-point add-ons run for a month at a time and extend from their end date when
            renewed early; one-time (“permanent”) fleet points stay with the account for as long as it
            stays open, whatever plan it is on. See the{" "}
            <Link to="/refund-policy" className="underline">Refund &amp; Billing Policy</Link>.
          </p>
        </div>
      </section>

      {/* TRIAL EXPLAINER */}
      <section className="px-4 sm:px-6 lg:px-8 mt-12 max-w-5xl mx-auto" data-testid="bp-trial-explainer">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 grid sm:grid-cols-3 gap-4">
          <TrialStep n="1" icon={Zap} title="30-day Pro trial"
                      body="Every new business starts on a 30-day free Pro trial: full Pro feature access, no card." />
          <TrialStep n="2" icon={Building2} title="Stay on Free if you want"
                      body="When the trial ends, your account is gracefully moved to the Free plan. No data is deleted." />
          <TrialStep n="3" icon={Users} title="Upgrade from your dashboard"
                      body="Switch to Basic, Pro, or Enterprise any time from your owner dashboard." />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 lg:px-8 mt-14 mb-20 max-w-5xl mx-auto" data-testid="bp-final-cta">
        <div className="rounded-2xl bg-[#1B4332] text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold">Ready to confirm bookings the right way?</h2>
            <p className="text-sm text-white/85 mt-1">
              Sign up, see your dashboard, and review the live business plan pricing.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/register?as=business" data-testid="bp-cta-trial-final"
                   className="inline-flex items-center px-5 py-2.5 rounded-lg bg-white text-[#1B4332] font-medium text-sm hover:bg-slate-100">
              Start free trial <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
            <Link to="/login?as=business"
                   data-testid="bp-cta-login-final"
                   className="inline-flex items-center px-5 py-2.5 rounded-lg border border-white/40 text-white text-sm font-medium hover:bg-white/10">
              Business login
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function TrialStep({ n, icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-[#1B4332] text-white text-xs font-bold flex items-center justify-center">{n}</div>
        <Icon className="w-4 h-4 text-[#1B4332]" />
        <h3 className="font-display text-sm font-semibold">{title}</h3>
      </div>
      <p className="text-xs text-slate-700 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
